import React, { useState } from "react";
import "./SetupBillingModal.scss";
import ReactModal from "react-modal";
import { Socket } from "socket.io-client";
import OwButton from "../../shared/OwButton/OwButton";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OnboardingBilling } from "../../onboarding/OnboardingBilling/OnboardingBilling";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import ILoadUserDTO from "../../../models/DTO/ILoadUserDTO";
import IStripeSubscriptionPaymentMethod from "../../../models/Stripe/IStripeSubscriptionPaymentMethod";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import UIService from "../../../services/UIService";
import UserTypesEnum from "../../../models/UserTypesEnum";

type Props = {
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    closeModalFn: () => void;
};

const dismissKey = (supplierGuid: string) => `setup-billing-dismissed-${supplierGuid}`;

/**
 * Whether to prompt this user to set up billing on login: a supplier admin of an onboarded, active account whose
 * Orderwerks subscription exists in Stripe but has had no payment method attached (has_setup_billing). Typical case:
 * a demo account that converted, so Orderwerks created its subscription after onboarding already ran.
 * "Remind me later" hides it for the rest of the browser session; it comes back on the next login.
 */
export const shouldPromptBillingSetup = (userModel: IUserUI | undefined, supplierModel: ISupplierModel | undefined): boolean => {
    if (!userModel?.guid || !supplierModel?.guid) {
        return false;
    }

    if (!userModel.isSupplierUser || userModel.userType?.id !== UserTypesEnum.ADMIN) {
        return false;
    }

    if (!supplierModel.has_onboarded || supplierModel.has_setup_billing || supplierModel.stripe_no_auto_pay) {
        return false;
    }

    if (!supplierModel.stripe_subscription_id || !supplierModel.subscription_active) {
        return false;
    }

    try {
        if (sessionStorage.getItem(dismissKey(supplierModel.guid))) {
            return false;
        }
    } catch {
        // sessionStorage unavailable: prompt anyway.
    }

    return true;
};

/**
 * Login prompt that reuses the onboarding billing step to put a payment method on the subscription Orderwerks
 * created for the business. Done reloads the user so the supplier model carries has_setup_billing.
 */
export const SetupBillingModal: React.FC<Props> = (props) => {
    const [ready, setReady] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<IStripeSubscriptionPaymentMethod | null>(null);

    const remindLater = () => {
        try {
            sessionStorage.setItem(dismissKey(props.supplierModel.guid), "1");
        } catch {
            // ignore
        }

        props.closeModalFn();
    };

    const done = () => {
        const payload: ILoadUserDTO = {
            userGuid: props.userModel.guid,
            userToken: props.userModel.token,
        };

        // Refresh the supplier model (has_setup_billing) so the prompt does not come back this session.
        props.socket.emit(EndpointsEnum.AppLoadUser, payload);

        props.closeModalFn();
    };

    const modalStyle = {
        overlay: {
            zIndex: 999999998,
        },
        content: {
            top: "6%",
            maxWidth: "36rem",
            margin: "0 auto",
            bottom: "auto",
            height: "auto",
            maxHeight: "88vh",
            overflow: "auto",
        },
    };

    return (
        <ReactModal
            isOpen={true}
            contentLabel="Set up billing"
            style={modalStyle}
            className={"modalClass"}
            overlayClassName={"overlayClass"}
            shouldCloseOnOverlayClick={false}
            onRequestClose={remindLater}
        >
            <div className={"SetupBillingModal p-2"}>
                <div className={"d-flex justify-content-between"}>
                    <OwTitle leftIconName={"fad fa-credit-card"} title={"set_up_billing"} tag={"h3"} />
                    <div>
                        <OwButton
                            className={"btn-danger"}
                            centerIconClass={"fas fa-times"}
                            fn={remindLater}
                            testId={"setup-billing-later"}
                        />
                    </div>
                </div>

                <div className={"mt-2 text-muted"}>
                    <OwLabel title={"billing_modal_intro"} />
                </div>

                <div className={"mt-3"}>
                    <OnboardingBilling
                        socket={props.socket}
                        notifyFn={props.notifyFn}
                        userModel={props.userModel}
                        supplierModel={props.supplierModel}
                        theme={UIService.isMainViewLight() ? "LIGHT" : "DARK"}
                        returnUrl={`${window.location.origin}/billing`}
                        onReadyChange={(isReady, method) => {
                            setReady(isReady);
                            setPaymentMethod(method);
                        }}
                        hideTitle={true}
                    />
                </div>

                <div className={"mt-4 d-flex"}>
                    <OwButton title={"done"} fn={done} disabled={!ready || !paymentMethod} testId={"setup-billing-done"} />
                    <OwButton className={"btn-secondary ms-3"} title={"remind_me_later"} fn={remindLater} />
                </div>
            </div>
        </ReactModal>
    );
};
